<script setup lang="ts">
import { useRefereshProductStore } from '@/products/commands/refreshProductStore';
import { useRefreshStorageAreaStore } from '@/storageAreas/stores/refreshStorageAreaStore';
import { storeToRefs } from 'pinia';
import { DataTable, Column } from 'primevue';
import { useProductGroupStore } from 'shared/products/groups/stores/productGroupStore';
import { useProductStore } from 'shared/products/stores/productStore';
import { useStorageAreaStore } from 'shared/storageAreas/stores/storageAreaStore';
import { useTranslations } from 'shared/translations/composables/useTranslations';
import { useLanguageStore } from 'shared/translations/stores/languageStore';
import { computed, onMounted } from 'vue';
import { useRefereshProductGroupStore } from '@/products/groups/commands/refreshProductGroupStore';

const storageAreaStore = useStorageAreaStore();
const storageAreas = storeToRefs(storageAreaStore).storageAreas;
const productStore = useProductStore();
const refreshProductStore = useRefereshProductStore();
const productGroupStore = useProductGroupStore();
const refreshProductGroupStore = useRefereshProductGroupStore();
const translations = useTranslations();
const languageStore = useLanguageStore();
const refreshStorageAreaStore = useRefreshStorageAreaStore();

const loading = computed(
  () => refreshStorageAreaStore.loading || refreshProductStore.loading || refreshProductGroupStore.loading,
);

onMounted(async () => {
  void Promise.all([
    refreshStorageAreaStore.initialize(),
    refreshProductStore.initialize(),
    refreshProductGroupStore.initialize(),
  ]);
});

const productGroups = computed<ProductGroupTableRow[]>(() => {
  const rows: ProductGroupTableRow[] = [];

  for (const storageArea of storageAreas.value) {
    if (!storageArea.logYardData) continue;
    for (const content of storageArea.logYardData.content) {
      const product = productStore.products.find((p) => p.id === content.productId);
      if (!product) continue;

      const productGroup = productGroupStore.groups.find((pg) => pg.id === product.productGroupId);
      let productGroupName = translations.value.generic.unknown;
      if (productGroup) {
        productGroupName = productGroup.name;
      }

      const existing = rows.find((pg) => pg.productGroupName === productGroupName);
      if (existing) {
        existing.numberOfLogs += content.numberOfLogs;
        existing.volume += content.volume;
      } else {
        rows.push({
          productGroupName: productGroupName,
          numberOfLogs: content.numberOfLogs,
          volume: content.volume,
        });
      }
    }
  }

  return rows.sort((a, b) => {
    if (a.productGroupName === translations.value.generic.unknown) return 1;
    if (b.productGroupName === translations.value.generic.unknown) return -1;
    return a.productGroupName.localeCompare(b.productGroupName);
  });
});

interface ProductGroupTableRow {
  productGroupName: string;
  numberOfLogs: number;
  volume: number;
}

function getVolume(row: ProductGroupTableRow) {
  return languageStore.formatNumber(row.volume, 1);
}

function getNumberOfLogs(row: ProductGroupTableRow) {
  return languageStore.formatNumber(row.numberOfLogs, 0);
}

const totalLogs = computed(() => {
  return languageStore.formatNumber(
    productGroups.value.reduce((sum, row) => sum + row.numberOfLogs, 0),
    0,
  );
});

const totalVolume = computed(() => {
  return languageStore.formatNumber(
    productGroups.value.reduce((sum, row) => sum + row.volume, 0),
    0,
  );
});
</script>

<template>
  <DataTable class="product-group-table" :value="productGroups" data-key="id" sort-field="name" scrollable
    :loading="loading" scrollHeight="flex">
    <template #header>
      <div class="flex flex-wrap items-center justify-between gap-2">
        <span class="text-xl font-bold">{{ translations.dashboard.productGroupTable.title }}</span>
      </div>
    </template>

    <template #empty>{{ translations.generic.noData }}</template>
    <template #loading>{{ translations.generic.loadingTableData }}</template>
    <Column field="productGroupName" :header="translations.generic.name" sortable sort-field="nameSortOrder">
      <template #footer>
        <strong>{{ translations.generic.total }}</strong>
      </template>
    </Column>
    <Column :field="getNumberOfLogs" :header="translations.generic.numberOfLogs" sortable>
      <template #footer>
        <strong>{{ totalLogs }}</strong>
      </template>
    </Column>
    <Column :field="getVolume" :header="translations.generic.volume" sortable>
      <template #footer>
        <strong>{{ totalVolume }}</strong>
      </template>
    </Column>
  </DataTable>
</template>

<style scoped>
:deep(.p-datatable-header div) {
  display: grid;
  grid-template-columns: auto 1fr auto auto;
}
</style>
